import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Sparkles, Lock, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/common/Card';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import api from '@/utils/api';

const AI_PROVIDERS = [
  { value: 'none', label: 'None', description: 'Configure AI later' },
  { value: 'groq', label: 'Groq (Recommended - Free)', description: 'Fast, free tier available' },
  { value: 'openai', label: 'OpenAI', description: 'GPT models, paid' },
  { value: 'anthropic', label: 'Anthropic', description: 'Claude models, paid' },
];

const AI_MODELS = {
  groq: [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B (Recommended)' },
    { value: 'llama-3.1-70b-versatile', label: 'Llama 3.1 70B' },
    { value: 'mixtral-8x7b-32768', label: 'Mixtral 8x7B' },
  ],
  openai: [
    { value: 'gpt-4-turbo-preview', label: 'GPT-4 Turbo' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  anthropic: [
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
  ],
};

export default function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    ai_provider: 'none',
    ai_model: '',
    ai_api_key: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleProviderChange = (provider) => {
    const models = AI_MODELS[provider];
    setFormData({
      ...formData,
      ai_provider: provider,
      ai_model: models ? models[0].value : '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (formData.ai_provider === 'none') {
        await api.put('/config', { ai_provider: 'none' });
      } else {
        await api.put('/config', formData);
      }
      setStep(3);
    } catch (error) {
      alert('Failed to save configuration: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = () => {
    navigate('/', { replace: true });
    window.location.reload(); // Reload to reinitialize with new config
  };

  const isAiConfigured = formData.ai_provider !== 'none';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/favicon.png" alt="Boswell" className="h-12 w-12" />
            <h1 className="text-4xl font-bold">Boswell</h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Your private email secretary
          </p>
          <p className="text-sm text-muted-foreground mt-1 italic">
            "I'm lost without my Boswell"
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
              step >= 1 ? 'border-primary bg-primary text-white' : 'border-muted'
            }`}>
              {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
            </div>
            <span className="text-sm font-medium">Welcome</span>
          </div>

          <div className="h-px w-12 bg-border" />

          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
              step >= 2 ? 'border-primary bg-primary text-white' : 'border-muted'
            }`}>
              {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
            </div>
            <span className="text-sm font-medium">Configure AI</span>
          </div>

          <div className="h-px w-12 bg-border" />

          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
              step >= 3 ? 'border-primary bg-primary text-white' : 'border-muted'
            }`}>
              {step > 3 ? <CheckCircle className="h-5 w-5" /> : '3'}
            </div>
            <span className="text-sm font-medium">Done</span>
          </div>
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Welcome to Boswell</CardTitle>
              <CardDescription>
                Your private email secretary - an intelligent brief of your inbox
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium mb-1">AI-Powered Categorization</h3>
                    <p className="text-sm text-muted-foreground">
                      Automatically categorizes and prioritizes your emails using advanced AI
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium mb-1">Multi-Account Support</h3>
                    <p className="text-sm text-muted-foreground">
                      Manage multiple IMAP email accounts in one place
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Lock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium mb-1">Privacy-Focused</h3>
                    <p className="text-sm text-muted-foreground">
                      Only metadata stored locally, email bodies fetched on-demand
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={() => setStep(2)} className="w-full">
                  Get Started
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: AI Configuration */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>AI Provider (optional)</CardTitle>
              <CardDescription>
                Boswell uses AI to analyze and categorize your emails. You can skip this and configure it later.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* AI Provider Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Select AI Provider</label>
                  <div className="grid gap-3">
                    {AI_PROVIDERS.map((provider) => (
                      <div
                        key={provider.value}
                        onClick={() => handleProviderChange(provider.value)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          formData.ai_provider === provider.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{provider.label}</h3>
                              {provider.value === 'groq' && (
                                <Badge variant="success" className="text-xs">Recommended</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {provider.description}
                            </p>
                          </div>
                          <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                            formData.ai_provider === provider.value
                              ? 'border-primary'
                              : 'border-muted'
                          }`}>
                            {formData.ai_provider === provider.value && (
                              <div className="h-3 w-3 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Model Selection - only show when AI is selected */}
                {isAiConfigured && (
                  <Select
                    label="AI Model"
                    value={formData.ai_model}
                    onChange={(e) => setFormData({ ...formData, ai_model: e.target.value })}
                  >
                    {AI_MODELS[formData.ai_provider].map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </Select>
                )}

                {/* API Key - only show when AI is selected */}
                {isAiConfigured && (
                  <div className="space-y-2">
                    <Input
                      label="API Key"
                      type="password"
                      placeholder={`Enter your ${formData.ai_provider} API key`}
                      value={formData.ai_api_key}
                      onChange={(e) => setFormData({ ...formData, ai_api_key: e.target.value })}
                      required
                    />

                    {/* API Key Help */}
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                      <p className="text-sm text-blue-900 dark:text-blue-100 mb-2">
                        <strong>Get your {formData.ai_provider === 'groq' ? 'Groq' : formData.ai_provider} API key:</strong>
                      </p>
                      {formData.ai_provider === 'groq' && (
                        <>
                          <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-4 list-decimal">
                            <li>Visit <a href="https://console.groq.com/" target="_blank" rel="noopener noreferrer" className="underline">console.groq.com</a></li>
                            <li>Sign up (free account)</li>
                            <li>Go to API Keys section</li>
                            <li>Create new key and copy it here</li>
                          </ol>
                        </>
                      )}
                      {formData.ai_provider === 'openai' && (
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Get your key at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com/api-keys</a>
                        </p>
                      )}
                      {formData.ai_provider === 'anthropic' && (
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Get your key at <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="underline">console.anthropic.com</a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {!isAiConfigured && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                    <p className="text-sm text-amber-900 dark:text-amber-100">
                      You can configure an AI provider later in Settings. Without AI, Boswell will work as a basic email client.
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    className="flex-1"
                  >
                    Continue
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Complete */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-green-500" />
                Setup Complete!
              </CardTitle>
              <CardDescription>
                Boswell is ready to assist you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {formData.ai_provider !== 'none' ? (
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <p className="text-sm text-green-900 dark:text-green-100">
                    ✅ AI provider configured successfully
                  </p>
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
                  <p className="text-sm text-amber-900 dark:text-amber-100">
                    ℹ️ AI not configured. You can set it up later in Settings when you're ready.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <h3 className="font-medium">Next Steps:</h3>
                <ol className="space-y-2 ml-4 list-decimal text-sm text-muted-foreground">
                  <li>Add your first IMAP email account</li>
                  <li>Let the agent sync your emails</li>
                  <li>Create filtering rules (optional)</li>
                  {formData.ai_provider !== 'none' && <li>Enjoy AI-powered email management!</li>}
                </ol>
              </div>

              <Button onClick={handleComplete} className="w-full">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
